import { forwardRef, useEffect, useRef, useState } from 'react';
import { KeyValue } from '@mews/enums';
import { InputValue } from '@optimus-web/common';
import { IconName } from '@optimus-web/icons';
import { BareIconButton } from '../BareIconButton';
import { Field } from '../Field';
import { Input } from '../Input';
import { CounterInputProps, HandleCounterInputValueChange } from './CounterInputProps';
import { ContainerElement, CounterButtonWrapper, FlexInputContainer } from './style';

enum MouseEventAction {
    DECREMENT = 'decrement',
    INCREMENT = 'increment',
}

const MOUSE_DOWN_INITIAL_CHANGE_DELAY = 350;
const MOUSE_DOWN_INTERVAL_CHANGE_SPEED = 40;

const CounterInputElement: React.RefForwardingComponent<HTMLInputElement, CounterInputProps> = (props, ref) => {
    const {
        className,
        disabled,
        max = Number.MAX_SAFE_INTEGER,
        min = Number.MIN_SAFE_INTEGER,
        name,
        onBlur,
        onChange,
        onFocus,
        readOnly,
        required,
        size = 'medium',
        step = 1,
        value: initialValue,
        ...other
    } = props;

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    const [value, setValue] = useState(initialValue || 0);
    const interval = useRef<number | null>(null);
    const timer = useRef<number | null>(null);

    const updateValueInInterval = (action: string) =>
        (interval.current = window.setInterval(() => {
            setValue((previousValue) =>
                getBoundedValue(action === MouseEventAction.INCREMENT ? previousValue + step : previousValue - step)
            );
        }, MOUSE_DOWN_INTERVAL_CHANGE_SPEED));

    const handleMouseDown = (action: MouseEventAction) => {
        timer.current = window.setTimeout(() => updateValueInInterval(action), MOUSE_DOWN_INITIAL_CHANGE_DELAY);
    };

    const handleMouseUp = (event: React.MouseEvent | React.KeyboardEvent, action: MouseEventAction) => {
        handleValueChange(event, action === MouseEventAction.INCREMENT ? value + step : value - step);
    };

    const handleValueChange: HandleCounterInputValueChange = (e, newValue) => {
        if (disabled) return;

        const boundedValue = getBoundedValue(newValue);
        setValue(boundedValue);
        onChange(e, boundedValue);
        clearTimers();
    };

    const handleChangeEvent = (
        e: React.ChangeEvent<HTMLInputElement> | React.MouseEvent | React.KeyboardEvent,
        inputValue: InputValue
    ) => {
        const parsedValue = parseInt(inputValue as string, 10);
        const newValue = Number.isNaN(parsedValue) ? 0 : parsedValue;
        handleValueChange(e, newValue);
    };

    const clearTimers = () => {
        window.clearInterval(interval.current ?? 0);
        window.clearTimeout(timer.current ?? 0);
    };

    const getBoundedValue = (valueToBound: number) => Math.max(min, Math.min(valueToBound, max));

    const inputProps = {
        disabled,
        name,
        onBlur,
        onChange: handleChangeEvent,
        onFocus,
        ref,
        readOnly,
        required,
        value,
        inputMode: 'numeric' as React.HTMLAttributes<HTMLInputElement>['inputMode'],
        inputSize: size,
        onKeyDown: (event: React.KeyboardEvent) => {
            if (event.key === KeyValue.ArrowUp) {
                handleMouseUp(event, MouseEventAction.INCREMENT);
            } else if (event.key === KeyValue.ArrowDown) {
                handleMouseUp(event, MouseEventAction.DECREMENT);
            }
        },
    };

    const isDecrementButtonDisabled = disabled || value <= min;
    const isIncrementButtonDisabled = disabled || value >= max;

    return (
        <ContainerElement size={size} className={className} {...other}>
            <CounterButtonWrapper
                disabled={isDecrementButtonDisabled}
                onMouseUp={(event) => handleMouseUp(event, MouseEventAction.DECREMENT)}
            >
                <BareIconButton
                    icon={IconName.MinusSimple}
                    onMouseDown={() => handleMouseDown(MouseEventAction.DECREMENT)}
                    disabled={isDecrementButtonDisabled}
                    tabIndex={-1}
                    data-test-button-minus
                />
            </CounterButtonWrapper>
            <FlexInputContainer>
                <Input {...inputProps} />
            </FlexInputContainer>
            <CounterButtonWrapper
                onMouseUp={(event) => handleMouseUp(event, MouseEventAction.INCREMENT)}
                disabled={isIncrementButtonDisabled}
            >
                <BareIconButton
                    icon={IconName.PlusSimple}
                    onMouseDown={() => handleMouseDown(MouseEventAction.INCREMENT)}
                    disabled={isIncrementButtonDisabled}
                    tabIndex={-1}
                    data-test-button-minus
                />
            </CounterButtonWrapper>
        </ContainerElement>
    );
};

const CounterInput = Field(forwardRef(CounterInputElement));

export default CounterInput;
